import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Lock, Eye, AlertTriangle, ChevronRight, Twitter, Instagram, Facebook, Linkedin } from "lucide-react";
import { useAuth } from "@/App";
import { ForcefieldLogo, ForcefieldLogoSmall } from "@/components/ForcefieldLogo";
import Marquee from "react-fast-marquee";

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Eye,
      title: "Privacy Analysis",
      description: "Identify exposed personal information across your social profiles"
    },
    {
      icon: Lock,
      title: "Account Security",
      description: "Check for vulnerabilities like weak 2FA and account settings"
    },
    {
      icon: AlertTriangle,
      title: "Risk Assessment",
      description: "Get a comprehensive risk score with actionable recommendations"
    }
  ];

  const platforms = [
    { icon: Twitter, name: "Twitter" },
    { icon: Instagram, name: "Instagram" },
    { icon: Facebook, name: "Facebook" },
    { icon: Linkedin, name: "LinkedIn" }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <ForcefieldLogo className="w-10 h-10" />
            <span className="font-outfit font-bold text-xl text-white tracking-tight">Social <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Forcefield</span></span>
          </Link>
          <nav className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/history">
                  <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="history-nav-btn">
                    History
                  </Button>
                </Link>
                <Link to="/analyze">
                  <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white rounded-full px-6" data-testid="analyze-nav-btn">
                    Start Analysis
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/auth">
                  <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="login-nav-btn">
                    Log In
                  </Button>
                </Link>
                <Link to="/auth?mode=signup">
                  <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white rounded-full px-6" data-testid="signup-nav-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-violet-600/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-fuchsia-600/10 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-6xl mx-auto relative">
          {/* Badge */}
          <div className="flex justify-start mb-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              <span className="text-sm text-gray-400">AI-Powered Security Analysis</span>
            </div>
          </div>

          {/* Headline */}
          <h1 className="font-outfit text-5xl md:text-7xl font-bold text-white tracking-tight mb-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            Protect Your<br />
            <span className="bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Digital Identity</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mb-12 animate-fade-up leading-relaxed" style={{ animationDelay: '0.2s' }}>
            Analyze your social media presence for security vulnerabilities. 
            Get actionable insights to shield yourself from digital threats.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <Button 
              onClick={() => navigate(isAuthenticated ? "/analyze" : "/auth?mode=signup")}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white text-lg px-8 py-6 rounded-full font-medium group"
              data-testid="hero-cta-btn"
            >
              Activate Your Forcefield
              <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              className="border-white/20 text-white hover:bg-white/5 text-lg px-8 py-6 rounded-full"
              data-testid="learn-more-btn"
            >
              Learn More
            </Button>
          </div>

          {/* Platform marquee */}
          <div className="mt-20 animate-fade-up" style={{ animationDelay: '0.4s' }}>
            <p className="text-sm text-gray-500 uppercase tracking-widest mb-6">Supported Platforms</p>
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10" />
              <Marquee speed={40} gradient={false} pauseOnHover={true}>
                {[...platforms, ...platforms, ...platforms].map((platform, i) => (
                  <div key={`${platform.name}-${i}`} className="flex items-center gap-3 text-gray-500 hover:text-gray-300 transition-colors mx-8">
                    <platform.icon className="w-6 h-6" />
                    <span className="text-sm font-medium">{platform.name}</span>
                  </div>
                ))}
              </Marquee>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-sm text-violet-400 uppercase tracking-widest mb-4">/features/</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white tracking-tight">
              Your Digital<br />Defense System
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 stagger-children">
            {features.map((feature, i) => (
              <div 
                key={feature.title}
                className="p-8 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-white/10 hover:bg-white/[0.04] transition-all opacity-0 animate-fade-up"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-sm text-violet-400 uppercase tracking-widest mb-4">/how it works/</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white tracking-tight">
              Activate Protection<br />in Minutes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Enter Handles", desc: "Input your social media usernames for Twitter, Instagram, Facebook, and LinkedIn" },
              { step: "02", title: "AI Analysis", desc: "Our powerful AI engine scans for security vulnerabilities and risks" },
              { step: "03", title: "Get Protected", desc: "Receive a detailed security report with risk scores and actionable steps" }
            ].map((item, i) => (
              <div key={item.step} className="relative">
                <div className="text-8xl font-outfit font-bold text-white/[0.03] absolute -top-6 -left-2">{item.step}</div>
                <div className="relative pt-12">
                  <h3 className="font-outfit text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white tracking-tight mb-6">
            Ready to Shield Your<br />Online Presence?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Activate your Social Forcefield and protect your digital identity today.
          </p>
          <Button 
            onClick={() => navigate(isAuthenticated ? "/analyze" : "/auth?mode=signup")}
            className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white text-lg px-8 py-6 rounded-full font-medium"
            data-testid="bottom-cta-btn"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ForcefieldLogoSmall className="w-7 h-7" />
            <span className="text-gray-400 text-sm font-medium">Social Forcefield</span>
          </div>
          <p className="text-gray-600 text-sm">
            powered by <a href="https://agentseven.ai" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">AgentSeven.ai</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
